<template>
  <div class="model-control">
    <div class="experiment-history-selection">
      <!-- Dataset selector for training -->
      <div class="experiment-history-selection" v-if="config['dataset_id']">
        <select v-model="config['dataset_id']">
          <option
            v-for="datasetId in datasetChoices"
            :key="datasetId"
            :value="datasetId"
          >
            Dataset: {{ datasetId }}
          </option>
        </select>
      </div>

      <!-- Experiment selector (per‐dataset or global) -->
      <select v-model="selectedExperimentId">
        <option
          v-for="(history, expId) in experimentsForDropdown"
          :key="expId"
          :value="expId"
        >
          {{ expId }} – {{ history.modelName }} – Loss:
          {{ history.loss.toFixed(4) }}
        </option>
      </select>
    </div>
    <!-- Status Display -->
    <div class="status-panel">
      <span>Status:</span>
      <span :class="getStatusClass(status)">{{ humanizeStatus(status) }}</span>
      <span v-if="status === 'running'" class="live-indicator">LIVE</span>

      <!-- ETA / Progress -->
      <template v-if="status === 'running' && currentStatusInfo">
        <!-- Fine-tuning: epochs -->
        <span
          v-if="
            task === 'training' && currentStatusInfo.current_epoch !== undefined
          "
        >
          Epoch: {{ currentStatusInfo.current_epoch }} /
          {{ currentStatusInfo.total_epochs }}
        </span>
        <!-- Active Learning: files -->
        <span
          v-else-if="
            task === 'active_learning' && currentStatusInfo.total_files
          "
        >
          Files: {{ currentStatusInfo.files_processed }} /
          {{ currentStatusInfo.total_files }}
        </span>
        <!-- ETA -->
        <span v-if="currentStatusInfo.eta_finish">
          ETA: {{ timeRemaining }}
        </span>
      </template>
    </div>

    <!-- Confidence vs. Error Scatter Plot -->
    <div class="confidence-container">
      <v-chart
        v-if="confidenceOption"
        :option="confidenceOption"
        autoresize
        class="confidence-chart"
      />
    </div>

    <!-- Action Buttons -->
    <div class="buttons">
      <button @click="openModal">New</button>
      <button v-if="status === 'stopped'" @click="startModel">Start</button>
      <button v-else-if="status !== 'running'" @click="resumeModel">
        Resume
      </button>
      <button v-else @click="pauseModel">Pause</button>
      <button v-if="status === 'running'" @click="stopModel">Stop</button>
    </div>
  </div>

  <!-- Modal (teleported to body) -->
  <teleport to="body">
    <div v-if="showModal" class="modal-overlay">
      <div class="modal">
        <h3>New {{ label }}</h3>

        <!-- Model selection -->
        <div class="param-row">
          <span>Model</span>
          <select v-model="paramSpecs.model_name" @change="fetchParamSpecs">
            <option v-for="m in modelChoices" :key="m" :value="m">
              {{ m }}
            </option>
          </select>
        </div>

        <label>
          <input type="checkbox" v-model="isGridSearch" /> Grid Search
        </label>

        <div class="param-list">
          <div v-for="spec in paramSpecs" :key="spec.name" class="param-row">
            <span>{{ humanize(spec.name) }}</span>

            <!-- non-grid: single value or choice -->
            <template v-if="!isGridSearch">
              <select v-if="spec.choices" v-model="config[spec.name]">
                <option
                  v-for="opt in spec.choices"
                  :key="opt"
                  :value="parseValue(opt, spec.type)"
                >
                  {{ opt }}
                </option>
              </select>
              <input
                v-else
                :type="spec.type === 'string' ? 'text' : 'number'"
                :step="getStep(spec)"
                v-model.number="config[spec.name]"
              />
            </template>

            <!-- grid: either comma list or checkboxes -->
            <template v-else>
              <div v-if="spec.choices" class="checkbox-group">
                <label v-for="opt in spec.choices" :key="opt">
                  <input
                    type="checkbox"
                    :value="parseValue(opt, spec.type)"
                    v-model="gridConfig[spec.name]"
                  />
                  {{ opt }}
                </label>
              </div>
              <input
                v-else
                type="text"
                v-model="gridConfig[spec.name]"
                placeholder="Comma-separated values"
              />
            </template>
          </div>
        </div>

        <div class="modal-buttons">
          <button @click="submitConfig">Submit</button>
          <button @click="closeModal">Cancel</button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import { api_config } from "@/env.ts";
import axios from "axios";
import { getToken } from "@/keycloak-wrapper.ts";
import VChart from "vue-echarts";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { ScatterChart } from "echarts/charts";
import {
  TooltipComponent,
  LegendComponent,
  GridComponent,
} from "echarts/components";

// register required ECharts modules
use([
  CanvasRenderer,
  ScatterChart,
  TooltipComponent,
  LegendComponent,
  GridComponent,
]);

export default {
  name: "ModelControl",
  components: { VChart },
  props: {
    label: { type: String, required: true },
    status: { type: String, required: true },
    endpoints: { type: Object, required: true },
    task: { type: String, required: true }, // e.g. 'training', 'active-learning', 'self-supervised'
    confidenceIntervals: { type: Object, required: false }, // e.g. 'training', 'active-learning', 'self-supervised'
    // status: {type: Object, required: true}, TODO: join all status' to prevent 3 duplicate requests
  },
  data() {
    return {
      token: null,
      showModal: false,
      isGridSearch: false,
      modelChoices: [],
      paramSpecs: [],
      config: {},
      gridConfig: {},
      statusInterval: null,
      currentStatusInfo: {},
      selectedExperimentId: null,
      experimentHistory: {
        // exp id: {loss: _, modelName: _,}
      },
      datasetChoices: [],
      selectedDatasetId: null,
      now: new Date(),
      confidenceOption: null,
    };
  },
  async mounted() {
    this.token = await getToken();
    await this.fetchModels();
    await this.fetchStatus();
    await this.fetchDatasets();
    this.fetchExperiments();
    this.fetchConfidence();
    this.nowInterval = setInterval(() => {
      this.now = new Date();
    }, 250);
  },
  beforeUnmount() {
    clearInterval(this.statusInterval);
    clearInterval(this.nowInterval);
    clearInterval(this.confidenceIntervalInterval);
  },
  computed: {
    timeRemaining() {
      if (!this.currentStatusInfo.eta_finish) return;
      const finish = new Date(this.currentStatusInfo.eta_finish);
      let diff = finish - this.now;
      if (diff <= 0) return "0s";
      const hrs = Math.floor(diff / 3600000);
      diff %= 3600000;
      const mins = Math.floor(diff / 60000);
      diff %= 60000;
      const secs = Math.floor(diff / 1000);
      return [hrs ? `${hrs}h` : "", mins ? `${mins}m` : "", `${secs}s`]
        .filter(Boolean)
        .join(" ");
    },
    isTrainingTask() {
      return this.task === "training";
    },
    experimentsForDropdown() {
      // if training, drill into the chosen dataset, else flat history
      return this.isTrainingTask
        ? this.experimentHistory[this.selectedDatasetId] || {}
        : this.experimentHistory;
    },
  },
  methods: {
    async fetchStatus() {
      // for most recent experiment, or running experiment.
      try {
        const res = await axios.get(`${api_config.ml_backend}/status`, {
          headers: this.authHeaders(),
        });
        const statusInfo = res.data;
        this.currentStatusInfo = statusInfo ? statusInfo[this.task] : {};
        this.selectedExperimentId = this.currentStatusInfo
          ? this.currentStatusInfo.experiment_id
          : null;
      } catch (e) {
        console.error("Error fetching status:", e);
        this.currentStatusInfo = {};
      }
    },
    async fetchDatasets() {
      try {
        const res = await axios.get(`${api_config.ml_backend}/datasets`, {
          headers: this.authHeaders(),
        });
        console.log("res:", res);
        this.datasetChoices = res.data.datasets.map((obj) => obj.DatasetId);
        console.log("this.datasetChoices:", this.datasetChoices);
      } catch (e) {
        console.error("Error fetching status:", e);
      }
    },
    async fetchStatusById() {
      try {
        const res = await axios.get(
          `${api_config.ml_backend}/${this.task}/status/${this.selectedExperimentId}`,
          { headers: this.authHeaders() }
        );
        this.currentStatusInfo = {};
        this.currentStatusInfo.status = res.data;
        this.$emit("update:status", res.data);
      } catch (e) {
        console.error("Error fetching status:", e);
        this.currentStatusInfo = {};
      }
    },
    humanize(key) {
      if (!key || typeof key !== "string") {
        return key;
      }
      return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    },
    humanizeStatus(s) {
      return this.humanize(s);
    },
    getStep(spec) {
      const key = spec.name;
      if (key.includes("lr")) return 0.0001;
      if (key.includes("drop") || key.includes("weight")) return 0.01;
      return 1;
    },
    authHeaders() {
      return {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.token}`,
      };
    },
    parseValue(val, type) {
      if (type === "integer") return parseInt(val, 10);
      if (type === "number") return parseFloat(val);
      return val;
    },
    async fetchModels() {
      try {
        const res = await axios.get(
          `${api_config.ml_backend}/${this.task}/models`,
          { headers: this.authHeaders() }
        );
        this.modelChoices = res.data.models;
        if (this.modelChoices.length) {
          this.paramSpecs.model_name = this.modelChoices[0];
          await this.fetchParamSpecs();
        }
      } catch (e) {
        console.error("Error fetching model list:", e);
      }
    },
    async fetchParamSpecs() {
      if (!this.paramSpecs.model_name) return;
      try {
        const res = await axios.get(
          `${api_config.ml_backend}/${this.task}/models/${this.paramSpecs.model_name}/params`,
          { headers: this.authHeaders() }
        );
        const model_name = this.paramSpecs.model_name;
        this.paramSpecs = res.data.parameters;
        this.paramSpecs.model_name = model_name;
        this.initConfigs();
        this.config.model_name = model_name;
      } catch (e) {
        console.error("Error fetching parameter specs:", e);
      }
    },
    async fetchExperiments() {
      if (!this.paramSpecs.model_name) return;
      try {
        const res = await axios.get(
          `${api_config.ml_backend}/${this.task}/experiments`,
          { headers: this.authHeaders() }
        );

        if (this.task === "training") {
          // group by DatasetId
          const byDataset = {};
          res.data.forEach((item) => {
            const d = item.DatasetId;
            if (!byDataset[d]) byDataset[d] = {};
            byDataset[d][item.ExperimentId] = {
              loss: item.Loss,
              modelName: item.ModelName,
            };
          });
          console.log("byDataset", byDataset);

          this.experimentHistory = byDataset;
          this.selectedDatasetId = this.datasetChoices.at(-1) || null;
          if (this.selectedDatasetId) {
            const exps = Object.keys(byDataset[this.selectedDatasetId]);
            this.selectedExperimentId = exps.at(-1) || null;
          }
        } else {
          // existing flat mapping
          this.experimentHistory = {};
          res.data.forEach((item) => {
            this.experimentHistory[item.ExperimentId] = {
              loss: item.Loss,
              modelName: item.ModelName,
            };
          });
        }
      } catch (e) {
        console.error("Error fetching parameter specs:", e);
      }
    },
    initConfigs() {
      this.config = { model_name: this.paramSpecs.model_name };
      this.gridConfig = {};
      this.paramSpecs.forEach((spec) => {
        // single-value defaults
        if (spec.choices) {
          this.config[spec.name] =
            spec.default !== undefined
              ? spec.default
              : this.parseValue(spec.choices[0], spec.type);
          this.gridConfig[spec.name] = [];
        } else {
          this.config[spec.name] =
            spec.default !== undefined
              ? spec.default
              : spec.type === "string"
              ? ""
              : 0;
          this.gridConfig[spec.name] = "";
        }
      });
    },
    async fetchConfidence() {
      if (!this.selectedExperimentId) {
        this.confidenceOption = null;
        return;
      }
      try {
        const res = await axios.get(
          `${api_config.ml_backend}/training/${this.selectedExperimentId}/confidence`,
          { headers: this.authHeaders() }
        );
        const data = res.data.confidence_intervals;
        this.updateConfidenceOption(data);
      } catch (e) {
        console.error("Error fetching confidence data:", e);
      }
    },
    updateConfidenceOption(data) {
      const series = data.map((r) => ({
        name: r.Class,
        type: "scatter",
        data: [
          [r.p90, 90],
          [r.p95, 95],
          [r.p99, 99],
          [r.p999, 99.9],
          [r.p9999, 99.99],
        ],
      }));
      this.confidenceOption = {
        legend: {
          data: data.map((r) => r.Class),
          top: "2%",
        },
        tooltip: {
          trigger: "item",
          formatter: (params) =>
            `${params.seriesName}<br/>Error: ${params.value[0].toFixed(
              2
            )} dBA<br/>Confidence: ${params.value[1]}%`,
        },
        xAxis: {
          type: "value",
          name: "Error (dBA)",
          nameLocation: "middle",
          nameGap: 20,
        },
        yAxis: {
          type: "value",
          name: "Confidence (%)",
          min: 90, // <-- lower limit
          max: 100, // <-- upper limit
          nameLocation: "middle",
          nameRotate: 90,
          nameGap: 25,
        },
        grid: {
          top: 60,
          left: 45,
          right: 15,
          bottom: 35,
        },
        series,
      };
    },
    openModal() {
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.isGridSearch = false;
      this.initConfigs();
    },
    async submitConfig() {
      let payload = {};
      this.currentStatusInfo = {};
      if (!this.isGridSearch) {
        // payload = { model_name: this.paramSpecs.model_name, config: this.config };
        payload = this.config;
      } else {
        const grid = {};
        this.paramSpecs.forEach((spec) => {
          const v = this.gridConfig[spec.name];
          if (spec.choices) {
            if (v.length) grid[spec.name] = v;
          } else if (v) {
            grid[spec.name] = v
              .split(",")
              .map((x) => this.parseValue(x.trim(), spec.type));
          }
        });
        payload = { model: this.paramSpecs.model_name, grid };
      }

      try {
        const res = await axios.post(
          `${api_config.ml_backend}${this.endpoints.start}`,
          payload,
          { headers: this.authHeaders() }
        );
        const status = res.data.status;
        const experiment_id = res.data.experiment_id;
        this.$emit("update:status", status);
        this.$emit("update:experimentId", experiment_id);
        this.closeModal();
      } catch (e) {
        console.error("Error starting model:", e);
      }
    },
    async startModel() {
      await this.submitConfig();
    },
    async resumeModel() {
      try {
        const res = await axios.post(
          `${api_config.ml_backend}${this.endpoints.resume}`,
          {},
          { headers: this.authHeaders() }
        );
        this.$emit("update:status", res.data.state.status);
      } catch (e) {
        console.error("Error resuming model:", e);
      }
    },
    async pauseModel() {
      try {
        const res = await axios.post(
          `${api_config.ml_backend}${this.endpoints.pause}`,
          {},
          { headers: this.authHeaders() }
        );
        this.$emit("update:status", res.data.state.status);
      } catch (e) {
        console.error("Error pausing model:", e);
      }
    },
    async stopModel() {
      try {
        await axios.post(
          `${api_config.ml_backend}${this.endpoints.stop}`,
          {},
          { headers: this.authHeaders() }
        );
        this.$emit("update:status", "stopped");
        this.$emit("update:experimentId", null);
      } catch (e) {
        console.error("Error stopping model:", e);
      }
    },
    getStatusClass(status) {
      if (status === "running") return "status-running";
      if (status === "paused" || status === "idle") return "status-paused";
      return "status-unknown";
    },
  },
  watch: {
    status(newStatus) {
      if (newStatus && newStatus === "running") {
        this.statusInterval = setInterval(this.fetchStatus, 10000);
      } else {
        clearInterval(this.statusInterval);
      }
    },
    async selectedExperimentId() {
      await this.fetchStatusById();
      await this.fetchConfidence();
      this.$emit("update:experimentId", this.selectedExperimentId);
    },
    confidenceIntervals(newValue) {
      this.updateConfidenceOption(newValue);
    },
    selectedDatasetId(newVal) {
      if (this.isTrainingTask && newVal) {
        const exps = Object.keys(this.experimentHistory[newVal] || {});
        this.selectedExperimentId = exps.at(-1) || null;
      }
    },
  },
};
</script>

<style scoped>
:root {
  --bg-overlay: rgba(0, 0, 0, 0.5);
  --transition-speed: 0.3s;
}

.model-control {
  /* margin: 1.5rem 0; */
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-rows: 1.875rem 3.75rem 1fr 2.5rem;
  gap: 0.4em;
  padding: 0.4em;
  overflow: auto;
}

.experiment-history-selection {
  height: 100%;
  display: flex;
  gap: var(--spacing-xs);
}

.experiment-history-selection select {
  height: 100%;
  border-radius: var(--radius-md);
}

.status-panel {
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  background-color: #f9fafb;
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: var(--radius-md);
}

.status-panel span {
  font-size: 1rem;
  color: #374151;
}

.status-running {
  color: var(--color-primary);
  font-weight: 600;
}

.status-paused {
  color: var(--warning-color);
  font-weight: 600;
}

.status-unknown {
  color: var(--danger-color);
  font-weight: 600;
}

.live-indicator {
  background-color: var(--danger-color);
  color: var(--colour-bg-light) !important;
  font-weight: 800;
  padding: 0.25rem 0.5rem;
  border-radius: var(--radius-md);
  /* font-size: 0.75rem; */
  /* text-transform: uppercase; */
}

.buttons {
  height: 100%;
  width: 100%;
  padding: 0 var(--spacing-2xl);
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
}

.buttons button {
  margin: 0;
  background-color: var(--primary-color);
  color: var(--colour-surface);
  border: none;
  padding: 0.5rem 1rem;
  border-radius: var(--radius-md);
  font-size: 0.9rem;
  cursor: pointer;
  transition: background-color var(--transition-speed),
    transform var(--transition-speed);
}

.buttons button:hover {
  background-color: var(--color-primary-hover);
  /* transform: translateY(-2px); */
}

.buttons button:disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--bg-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: var(--colour-surface);
  padding: 2rem;
  border-radius: var(--radius-md);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
}

.param-row {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 1rem;
  align-items: center;
  /* margin-bottom: 1rem; */
}

.checkbox-group {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.param-list {
  /* margin-top: 1rem; */
}

.modal-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  /* margin-top: 1.5rem; */
}

.modal-buttons button {
  background-color: var(--colour-surface);
  color: var(--primary-color);
  border: 2px solid var(--primary-color);
  padding: 0.5rem 1rem;
  border-radius: var(--radius-md);
  font-size: 0.9rem;
  cursor: pointer;
  transition: background-color var(--transition-speed),
    color var(--transition-speed);
}

.modal-buttons button:hover {
  background-color: var(--primary-color);
  color: var(--colour-surface);
}

.confidence-container {
  height: 100%;
  width: 100%;
  background-color: var(--colour-surface);
  border-radius: var(--radius-md);
  border: 2px solid #aaa;
}
.confidence-chart {
  width: 100%;
  height: 100%;
  /* margin-top: 1rem; */
}
</style>
